import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Avatar,
  Box,
  Card,
  CardContent,
  Checkbox,
  Divider,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  FormControlLabel,
  Grid,
  IconButton,
  InputBase,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import BusinessIcon from '@material-ui/icons/Business';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SearchIcon from '@material-ui/icons/Search';
import UnfoldLessIcon from '@material-ui/icons/UnfoldLess';
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';
import ViewWeekIcon from '@material-ui/icons/ViewWeek';

import { DialogoCreateEdit } from '../../Components/modulo-gerencial-dialogos';
import {
  TextInput,
  SwitchInput,
} from '../../Components/modulo-gerencial-formularios';
import { useForm } from '../../Components/modulo-gerencial-formularios/hooks';
import { permissoes } from '../../utils/dadosTelas';
import { permissoesHierarchy } from '../../utils/permissionsHierarchy';

const initialValues = {
  nome: '',
  descricao: '',
  ...permissoes.reduce((acc, permissao) => {
    acc[permissao.chave] = false;
    return acc;
  }, {}),
};

const validadores = {
  nome: (value, values, config) => {
    if (value.length < 2) return 'Não pode ser menor que 2';
    if (value.length > 100) return 'Não pode ser maior que 100';
    return '';
  },
  descricao: (value, values, config) => {
    if (value.length < 2) return 'Não pode ser menor que 2';
    if (value.length > 255) return 'Não pode ser maior que 255';
    return '';
  },
};

function formataPermissoes(permissoesBD) {
  const mapaPermissoesBD = permissoesBD.reduce((acc, permissao) => {
    acc[permissao.chave] = permissao.chave;
    return acc;
  }, {});

  return permissoes.reduce((acc, permissao) => {
    acc[permissao.chave] = mapaPermissoesBD[permissao.chave] ? true : false;
    return acc;
  }, {});
}

const transformGetDados = (dados, config) => ({
  nome: dados.nome,
  descricao: dados.descricao,
  ...formataPermissoes(dados.permissoes),
});

const transformSalvar = ({ nome, descricao, ...permissoes }, config) => ({
  nome,
  descricao,
  permissoes,
});

const TOOLBAR_HEIGHT = 46;
const APP_HEADER_HEIGHT = 46;

const useStyles = makeStyles((theme) => ({
  formHeader: {
    padding: theme.spacing(2),
    backgroundColor: '#f8faff',
    borderRadius: 10,
    border: '1px solid #e4e8f7',
    marginBottom: theme.spacing(1.5),
  },

  permissoesToolbar: {
    position: 'sticky',
    top: 0,
    zIndex: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    height: TOOLBAR_HEIGHT,
    backgroundColor: '#fff',
    borderBottom: '1px solid #e4e8f7',
    borderTop: '1px solid #e4e8f7',
    marginBottom: theme.spacing(1.5),
    paddingRight: 4,
  },

  permissoesTitulo: {
    fontWeight: 800,
    fontSize: '0.8rem',
    color: '#4a5fa8',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    whiteSpace: 'nowrap',
    padding: '0 12px',
    borderRight: '1px solid #e4e8f7',
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
  },

  navBar: {
    display: 'flex',
    gap: 4,
    overflowX: 'auto',
    alignItems: 'center',
    width: '100%',
    '&::-webkit-scrollbar': { height: 0 },
  },

  navApp: {
    flexShrink: 0,
    padding: '3px 10px',
    borderRadius: 20,
    border: '1px solid #c5cff7',
    background: 'none',
    color: '#667eea',
    fontSize: '0.72rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all .15s',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#eef1ff',
      borderColor: '#a5b4fc',
    },
  },

  appPanel: {
    marginBottom: theme.spacing(1.5),
    border: 'none',
    borderRadius: 10,
    boxShadow: '0 2px 6px rgba(20, 40, 120, 0.10)',
    overflow: 'clip',

    '&:before': {
      display: 'none',
    },
  },

  appSummary: {
    position: 'sticky',
    top: TOOLBAR_HEIGHT,
    zIndex: 10,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    minHeight: `${APP_HEADER_HEIGHT}px !important`,
    padding: '0 12px',
    borderRadius: 10,

    '&$expanded': {
      minHeight: `${APP_HEADER_HEIGHT}px !important`,
      borderRadius: '10px 10px 0 0',
    },

    '& .MuiExpansionPanelSummary-content': {
      margin: '6px 0',
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
    },

    '& .MuiExpansionPanelSummary-content.Mui-expanded': {
      margin: '6px 0',
    },

    '& .MuiIconButton-root': {
      padding: 4,
      color: '#fff',
    },
  },

  modulePanel: {
    marginBottom: theme.spacing(1),
    border: '1.5px solid #e8edf7',
    borderRadius: 8,
    boxShadow: 'none',
    overflow: 'clip',
    backgroundColor: '#f9fafc',

    '&:before': {
      display: 'none',
    },
  },

  moduleSummary: {
    position: 'sticky',
    top: TOOLBAR_HEIGHT + APP_HEADER_HEIGHT,
    zIndex: 9,
    backgroundColor: '#f0f3ff',
    minHeight: '40px !important',
    padding: '0 12px',
    borderLeft: '3px solid #667eea',

    '&$expanded': {
      minHeight: '40px !important',
    },

    '& .MuiExpansionPanelSummary-content': {
      margin: '6px 0',
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
    },

    '& .MuiExpansionPanelSummary-content.Mui-expanded': {
      margin: '6px 0',
    },

    '& .MuiIconButton-root': {
      padding: 4,
    },
  },

  moduleDetails: {
    backgroundColor: '#fafbfe',
    padding: theme.spacing(1.5),
  },

  telaCard: {
    height: '100%',
    borderRadius: 7,
    border: '1px solid #e0e4eb',
    boxShadow: 'none',
    backgroundColor: '#fff',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
  },

  telaCardContent: {
    padding: '8px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    '&:last-child': {
      paddingBottom: '8px',
    },
  },

  headerLinha: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },

  headerLinhaTituloTela: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '36px',
    background: 'linear-gradient(135deg, #f0f3ff 0%, #f8f5ff 100%)',
    borderRadius: '6px 6px 0 0',
    gap: '4px',
    padding: '0 6px',
    borderBottom: '1px solid #e4e8f7',
    //color: '#4aa874',
  },

  tituloTela: {
    fontWeight: 700,
    fontSize: '0.72rem',
    //color: '#4a5fa8',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1,
    minWidth: 0,
    color: '#9a1a8d',
  },

  iconeTela: {
    fontSize: '0.85rem',
    color: '#667eea',
    flexShrink: 0,
  },

  avatarApp: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: '#fff',
    width: 32,
    height: 32,
  },

  avatarModulo: {
    backgroundColor: '#e8edf7',
    color: '#667eea',
    width: 32,
    height: 28,
  },

  tituloApp: {
    fontWeight: 800,
    fontSize: '0.9rem',
    color: '#fff',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  tituloModulo: {
    fontWeight: 700,
    fontSize: '0.85rem',
    color: '#667eea',
  },

  divisor: {
    margin: '6px 0',
    height: '1px',
  },

  switchLinha: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },

  grupoTitulo: {
    fontWeight: 700,
    fontSize: '0.65rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#75ea66',
  },

  campoBusca: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f0f3ff',
    border: '1.5px solid #c5cff7',
    borderRadius: 8,
    padding: '0 6px 0 8px',
    height: 30,
    gap: 4,
    flexShrink: 0,
    transition: 'border-color .15s, background-color .15s',
    '&:focus-within': {
      borderColor: '#667eea',
      backgroundColor: '#eef1ff',
    },
  },

  inputBusca: {
    fontSize: '0.75rem',
    color: '#4a5fa8',
    width: 170,
    '& input::placeholder': {
      color: '#94a3b8',
      opacity: 1,
    },
  },

  badgeResultados: {
    flexShrink: 0,
    backgroundColor: '#667eea',
    color: '#fff',
    borderRadius: 10,
    padding: '1px 8px',
    fontSize: '0.65rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },

  botoesExpandir: {
    display: 'flex',
    border: '1.5px solid #c5cff7',
    borderRadius: 8,
    overflow: 'hidden',
    flexShrink: 0,
  },

  btnExpandir: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 10px',
    background: 'none',
    border: 'none',
    borderRight: '1.5px solid #c5cff7',
    cursor: 'pointer',
    color: '#667eea',
    fontWeight: 600,
    fontSize: '0.72rem',
    transition: 'background .15s',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#eef1ff',
    },
    '&:last-child': {
      borderRight: 'none',
    },
  },
}));

function buildGruposTree(grupos) {
  const byPath = {};
  grupos.forEach((grupo) => {
    byPath[grupo.caminho] = { ...grupo, filhos: [] };
  });

  const roots = [];
  grupos.forEach((grupo) => {
    const partes = grupo.caminho.split('.');
    const parentPath = partes.slice(0, -1).join('.');
    if (parentPath && byPath[parentPath]) {
      byPath[parentPath].filhos.push(byPath[grupo.caminho]);
    } else {
      roots.push(byPath[grupo.caminho]);
    }
  });

  return roots;
}

function getAllChavesGrupo(grupoNode) {
  return [
    ...grupoNode.permissoes.map((p) => p.chave),
    ...grupoNode.filhos.flatMap((filho) => getAllChavesGrupo(filho)),
  ];
}

function getChaves(modulos) {
  return modulos.flatMap(({ telas }) =>
    telas.flatMap(({ permissoes: ps, grupos: gs }) => [
      ...ps.map((p) => p.chave),
      ...gs.flatMap((g) => g.permissoes.map((p) => p.chave)),
    ]),
  );
}

function getChavesTelas(telas) {
  return telas.flatMap(({ permissoes: ps, grupos: gs }) => [
    ...ps.map((p) => p.chave),
    ...gs.flatMap((g) => g.permissoes.map((p) => p.chave)),
  ]);
}

function prepararGruposTree(grupos) {
  const gruposTree = buildGruposTree(grupos);

  const hidratar = (grupo) => {
    const filhos = grupo.filhos.map(hidratar);
    const chavesGrupo = [
      ...grupo.permissoes.map((p) => p.chave),
      ...filhos.flatMap((filho) => filho.chavesGrupo),
    ];

    return {
      ...grupo,
      filhos,
      chavesGrupo,
    };
  };

  return gruposTree.map(hidratar);
}

function prepararPermissoesAgrupadas(agrupadas) {
  return agrupadas.map((appItem) => {
    const modulos = appItem.modulos.map((moduloItem) => {
      const telas = moduloItem.telas.map((telaItem) => {
        const gruposTree = prepararGruposTree(telaItem.grupos);
        const chavesCard = [
          ...telaItem.permissoes.map((p) => p.chave),
          ...gruposTree.flatMap((grupo) => grupo.chavesGrupo),
        ];

        return {
          ...telaItem,
          gruposTree,
          chavesCard,
        };
      });

      return {
        ...moduloItem,
        telas,
        chavesModulo: getChavesTelas(telas),
      };
    });

    return {
      ...appItem,
      modulos,
      chavesApp: getChaves(modulos),
    };
  });
}

function criarEstadoExpandidos(todasChaves, valor) {
  return todasChaves.reduce((acc, chave) => {
    acc[chave] = valor;
    return acc;
  }, {});
}

function criarEstadoInicialExpandidos(todasChaves) {
  return todasChaves.reduce((acc, chave) => {
    // Abre apenas os apps. Os módulos ficam fechados para reduzir o custo inicial.
    acc[chave] = !chave.includes('.');
    return acc;
  }, {});
}

function filtrarPermissoes(agrupadas, busca) {
  const termo = busca.toLowerCase().trim();
  if (!termo) return agrupadas;

  const match = (text) => (text || '').toLowerCase().includes(termo);

  const filtrarTela = (telaItem) => {
    const { nomeTela, permissoes: permsSimples, grupos } = telaItem;

    const telaMatch = match(nomeTela);

    const permsFiltradas = telaMatch
      ? permsSimples
      : permsSimples.filter((p) => match(p.acao));

    const gruposFiltrados = grupos
      .map((grupo) => {
        const grupoMatch = telaMatch || match(grupo.descricao);
        return {
          ...grupo,
          permissoes: grupoMatch
            ? grupo.permissoes
            : grupo.permissoes.filter((p) => match(p.acao)),
        };
      })
      .filter((g) => g.permissoes.length > 0);

    if (!permsFiltradas.length && !gruposFiltrados.length) return null;

    const gruposTree = prepararGruposTree(gruposFiltrados);
    const chavesCard = [
      ...permsFiltradas.map((p) => p.chave),
      ...gruposTree.flatMap((grupo) => grupo.chavesGrupo),
    ];

    return {
      ...telaItem,
      permissoes: permsFiltradas,
      grupos: gruposFiltrados,
      gruposTree,
      chavesCard,
    };
  };

  return agrupadas
    .map((appItem) => ({
      ...appItem,
      modulos: appItem.modulos
        .map((moduloItem) => {
          const moduloMatch = match(moduloItem.nomeModulo);
          const telas = moduloMatch
            ? moduloItem.telas
            : moduloItem.telas.map(filtrarTela).filter(Boolean);

          return {
            ...moduloItem,
            telas,
            chavesModulo: getChavesTelas(telas),
          };
        })
        .filter((m) => m.telas.length > 0),
    }))
    .map((appItem) => ({
      ...appItem,
      chavesApp: getChaves(appItem.modulos),
    }))
    .filter((a) => a.modulos.length > 0);
}

const TermoBuscaContext = React.createContext('');

function TextHighlight({ text, termo }) {
  if (!termo || !text) return <>{text}</>;

  const t = termo.toLowerCase();
  const str = String(text);
  const lower = str.toLowerCase();
  const parts = [];
  let lastIdx = 0;
  let idx = lower.indexOf(t, lastIdx);

  while (idx !== -1) {
    if (idx > lastIdx) parts.push(str.slice(lastIdx, idx));
    parts.push(
      <mark
        key={idx}
        style={{
          backgroundColor: '#fed7aa',
          color: '#c2410c',
          borderRadius: 2,
          padding: '0 1px',
          fontWeight: 700,
        }}
      >
        {str.slice(idx, idx + t.length)}
      </mark>,
    );
    lastIdx = idx + t.length;
    idx = lower.indexOf(t, lastIdx);
  }

  if (lastIdx < str.length) parts.push(str.slice(lastIdx));
  return <>{parts}</>;
}

function MarcadorTodos({ chaves, light = false }) {
  const { getValue, setValue, subscribeField } = useForm();
  const [, forceUpdate] = useState({});
  const chavesKey = chaves.join(',');

  useEffect(() => {
    const unsubs = chaves.map((chave) =>
      subscribeField(chave, () => forceUpdate({})),
    );
    return () => unsubs.forEach((u) => u());
  }, [chavesKey, chaves, subscribeField]);

  const valores = chaves.map((c) => getValue(c));
  const todos = valores.length > 0 && valores.every(Boolean);
  const algum = valores.some(Boolean);

  const handleChange = () => {
    const novoValor = !todos;
    chaves.forEach((chave) => setValue(chave, novoValor));
  };

  const cor = light ? '#fff' : '#667eea';

  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={todos}
          indeterminate={!todos && algum}
          onChange={handleChange}
          size="small"
          style={{ padding: '2px 4px', color: cor }}
          onClick={(e) => e.stopPropagation()}
        />
      }
      label={
        <Typography variant="caption" style={{ fontWeight: 600, color: cor }}>
          Todos
        </Typography>
      }
      style={{ margin: 0 }}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

function ProgressoPermissoes({ chaves, light = false }) {
  const { getValue, subscribeField } = useForm();
  const [, forceUpdate] = useState({});
  const chavesKey = chaves.join(',');

  useEffect(() => {
    const unsubs = chaves.map((c) => subscribeField(c, () => forceUpdate({})));
    return () => unsubs.forEach((u) => u());
  }, [chavesKey, chaves, subscribeField]);

  const total = chaves.length;
  const ativos = chaves.filter((c) => getValue(c)).length;
  const pct = total > 0 ? ativos / total : 0;

  const cor = light ? 'rgba(255,255,255,0.9)' : '#667eea';
  const corBg = light ? 'rgba(255,255,255,0.25)' : '#d4daff';
  const corBar = light ? '#fff' : '#667eea';

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 3,
        minWidth: 52,
        flexShrink: 0,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Typography
        style={{
          fontSize: '0.68rem',
          fontWeight: 700,
          color: cor,
          lineHeight: 1,
        }}
      >
        {ativos}/{total}
      </Typography>
      <Box
        style={{
          width: 52,
          height: 3,
          backgroundColor: corBg,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          style={{
            width: `${pct * 100}%`,
            height: '100%',
            backgroundColor: corBar,
            borderRadius: 2,
            transition: 'width 0.3s ease',
          }}
        />
      </Box>
    </Box>
  );
}

const GRUPO_ACCENTS = ['#667eea', '#a5b4fc', '#c7d2fe'];
const GRUPO_HEADER_BG = ['#edf0fb', '#f3f5ff', '#f7f8ff'];
const GRUPO_CONTAINER_BG = ['#f8faff', '#fafbff', '#fcfcff'];
const GRUPO_BORDER = ['#c5cff7', '#d4daff', '#e4e8ff'];

function GrupoNode({ grupo, depth }) {
  const classes = useStyles();
  const termo = React.useContext(TermoBuscaContext);
  const chaves = grupo.chavesGrupo || getAllChavesGrupo(grupo);
  const label = grupo.niveis[grupo.niveis.length - 1].descricao;

  const d = Math.min(depth, 2);
  const accent = GRUPO_ACCENTS[d];
  const headerBg = GRUPO_HEADER_BG[d];
  const containerBg = GRUPO_CONTAINER_BG[d];
  const border = GRUPO_BORDER[d];

  return (
    <Box
      style={{
        marginTop: depth === 0 ? 8 : 5,
        marginLeft: depth * 10,
        borderRadius: '0 6px 6px 0',
        border: `1px solid ${border}`,
        borderLeft: `3px solid ${accent}`,
        overflow: 'hidden',
        backgroundColor: containerBg,
      }}
    >
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '3px 6px 3px 10px',
          backgroundColor: headerBg,
          borderBottom: `1px solid ${border}`,
        }}
      >
        <Typography className={classes.grupoTitulo} style={{ color: accent }}>
          <TextHighlight text={label} termo={termo} />
        </Typography>
        <MarcadorTodos chaves={chaves} />
      </Box>

      <Box
        style={{
          padding: '6px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {grupo.permissoes.map((permissao) => (
          <SwitchInput
            identificador={permissao.chave}
            key={permissao.chave}
            name={permissao.chave}
            label={<TextHighlight text={permissao.acao} termo={termo} />}
          />
        ))}

        {grupo.filhos.map((filho) => (
          <GrupoNode key={filho.caminho} grupo={filho} depth={depth + 1} />
        ))}
      </Box>
    </Box>
  );
}

function Form() {
  const classes = useStyles();
  const appRefs = useRef({});

  const permissoesAgrupadas = useMemo(
    () => prepararPermissoesAgrupadas(permissoesHierarchy(permissoes)),
    [],
  );

  const todasChaves = useMemo(() => {
    const chaves = [];

    permissoesAgrupadas.forEach(({ app, modulos }) => {
      chaves.push(app);

      modulos.forEach(({ modulo }) => {
        chaves.push(`${app}.${modulo}`);
      });
    });

    return chaves;
  }, [permissoesAgrupadas]);

  const [busca, setBusca] = useState('');
  const [buscaDebounced, setBuscaDebounced] = useState('');
  const [expandidos, setExpandidos] = useState(() =>
    criarEstadoInicialExpandidos(todasChaves),
  );

  const termoBusca = buscaDebounced.trim();
  const buscaAtiva = !!termoBusca;
  // Evita abrir muitos painéis quando o usuário digita só 1 caractere.
  const expandirPorBusca = termoBusca.length >= 2;
  const buscaDebouncedRef = useRef('');

  useEffect(() => {
    const timer = setTimeout(() => setBuscaDebounced(busca), 300);
    return () => clearTimeout(timer);
  }, [busca]);

  useEffect(() => {
    const prev = buscaDebouncedRef.current;
    buscaDebouncedRef.current = buscaDebounced;

    if (prev.trim() && !buscaDebounced.trim()) {
      setExpandidos((ant) => {
        const prox = { ...ant };
        Object.keys(prox).forEach((chave) => {
          if (chave.includes('.')) prox[chave] = false;
        });
        return prox;
      });
    }
  }, [buscaDebounced]);

  const permissoesFiltradas = useMemo(
    () => filtrarPermissoes(permissoesAgrupadas, buscaDebounced),
    [permissoesAgrupadas, buscaDebounced],
  );

  const totalResultados = useMemo(() => {
    if (!buscaAtiva) return 0;

    return permissoesFiltradas.reduce(
      (acc, { modulos }) =>
        acc +
        modulos.reduce(
          (a, { telas }) =>
            a +
            telas.reduce(
              (b, { permissoes: ps, grupos: gs }) =>
                b + ps.length + gs.reduce((c, g) => c + g.permissoes.length, 0),
              0,
            ),
          0,
        ),
      0,
    );
  }, [permissoesFiltradas, buscaAtiva]);

  const scrollParaApp = useCallback((app) => {
    appRefs.current[app]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  const toggleExpandido = useCallback(
    (chave) => (event, expanded) => {
      setExpandidos((prev) => {
        if (prev[chave] === expanded) return prev;
        return {
          ...prev,
          [chave]: expanded,
        };
      });
    },
    [],
  );

  const expandirTudo = useCallback(() => {
    setExpandidos(criarEstadoExpandidos(todasChaves, true));
  }, [todasChaves]);

  const recolherTudo = useCallback(() => {
    setExpandidos(criarEstadoExpandidos(todasChaves, false));
  }, [todasChaves]);

  return (
    <Box>
      <Box className={classes.formHeader}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextInput name="nome" fullWidth label="Nome" autoFocus />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextInput name="descricao" fullWidth label="Descrição" />
          </Grid>
        </Grid>
      </Box>

      <Box className={classes.permissoesToolbar}>
        <Typography className={classes.permissoesTitulo}>Permissões</Typography>

        <Box className={classes.campoBusca}>
          <SearchIcon
            style={{
              fontSize: 15,
              color: busca ? '#667eea' : '#94a3b8',
              flexShrink: 0,
            }}
          />
          <InputBase
            className={classes.inputBusca}
            placeholder="Buscar permissão..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          {busca && (
            <IconButton
              size="small"
              onClick={() => setBusca('')}
              style={{ padding: 2 }}
            >
              <CloseIcon style={{ fontSize: 13, color: '#667eea' }} />
            </IconButton>
          )}
        </Box>

        <Box style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          {buscaAtiva ? (
            <Box className={classes.navBar}>
              <Typography className={classes.badgeResultados}>
                {totalResultados} resultados
              </Typography>
            </Box>
          ) : (
            <Box className={classes.navBar}>
              {permissoesAgrupadas.map(({ app, nomeApp }) => (
                <button
                  key={app}
                  type="button"
                  className={classes.navApp}
                  onClick={() => scrollParaApp(app)}
                >
                  {nomeApp}
                </button>
              ))}
            </Box>
          )}
        </Box>

        <Box className={classes.botoesExpandir}>
          <button
            type="button"
            className={classes.btnExpandir}
            onClick={expandirTudo}
          >
            <UnfoldMoreIcon style={{ fontSize: 14 }} />
            Expandir
          </button>

          <button
            type="button"
            className={classes.btnExpandir}
            onClick={recolherTudo}
          >
            <UnfoldLessIcon style={{ fontSize: 14 }} />
            Recolher
          </button>
        </Box>
      </Box>

      <TermoBuscaContext.Provider value={buscaDebounced}>
        {permissoesFiltradas.map(({ app, nomeApp, modulos, chavesApp }) => (
          <Box
            key={app}
            ref={(el) => {
              appRefs.current[app] = el;
            }}
          >
            <ExpansionPanel
              className={classes.appPanel}
              TransitionProps={{ unmountOnExit: true }}
              expanded={expandirPorBusca ? true : !!expandidos[app]}
              onChange={toggleExpandido(app)}
            >
              <ExpansionPanelSummary
                expandIcon={<ExpandMoreIcon />}
                className={classes.appSummary}
              >
                <Box className={classes.headerLinha}>
                  <Avatar className={classes.avatarApp}>
                    <BusinessIcon />
                  </Avatar>

                  <Typography variant="h6" className={classes.tituloApp}>
                    {nomeApp}
                  </Typography>

                  <Box
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.18)',
                      color: 'rgba(255,255,255,0.85)',
                      borderRadius: 10,
                      padding: '1px 8px',
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {modulos.length} módulo{modulos.length !== 1 ? 's' : ''}
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                  {/* <ProgressoPermissoes chaves={chavesApp} light /> */}
                  <MarcadorTodos chaves={chavesApp} light />
                </Box>
              </ExpansionPanelSummary>

              <ExpansionPanelDetails>
                <Box width="100%">
                  {modulos.map(
                    (
                      { modulo, nomeModulo, telas, chavesModulo },
                      moduloIdx,
                    ) => (
                      <ExpansionPanel
                        key={`${app}.${modulo}`}
                        className={classes.modulePanel}
                        TransitionProps={{ unmountOnExit: true }}
                        expanded={
                          expandirPorBusca
                            ? true
                            : !!expandidos[`${app}.${modulo}`]
                        }
                        onChange={toggleExpandido(`${app}.${modulo}`)}
                      >
                        <ExpansionPanelSummary
                          expandIcon={<ExpandMoreIcon />}
                          className={classes.moduleSummary}
                        >
                          <Box className={classes.headerLinha}>
                            <Avatar className={classes.avatarModulo}>
                              <Typography
                                style={{
                                  fontSize: '0.7rem',
                                  fontWeight: 800,
                                  lineHeight: 1,
                                  color: '#86267e',
                                }}
                              >
                                {moduloIdx + 1}/{modulos.length}
                              </Typography>
                            </Avatar>

                            <Typography
                              variant="subtitle1"
                              className={classes.tituloModulo}
                            >
                              <TextHighlight
                                text={nomeModulo}
                                termo={buscaDebounced}
                              />
                            </Typography>
                          </Box>

                          <Box
                            display="flex"
                            alignItems="center"
                            style={{ gap: 8 }}
                          >
                            {/* <ProgressoPermissoes chaves={chavesModulo} /> */}
                            <MarcadorTodos chaves={chavesModulo} />
                          </Box>
                        </ExpansionPanelSummary>

                        <ExpansionPanelDetails
                          className={classes.moduleDetails}
                        >
                          <Grid container spacing={2}>
                            {telas.map(
                              ({
                                tela,
                                nomeTela,
                                permissoes: permsTelaSimples,
                                gruposTree,
                                chavesCard,
                              }) => (
                                <Grid
                                  item
                                  xs={12}
                                  sm={6}
                                  md={3}
                                  lg={2.4}
                                  key={`${app}.${modulo}.${tela}`}
                                >
                                  <Card className={classes.telaCard}>
                                    <CardContent
                                      className={classes.telaCardContent}
                                    >
                                      <Box
                                        className={
                                          classes.headerLinhaTituloTela
                                        }
                                      >
                                        <ViewWeekIcon
                                          className={classes.iconeTela}
                                        />
                                        <Tooltip
                                          title={String(nomeTela)}
                                          placement="top"
                                        >
                                          <Typography
                                            className={classes.tituloTela}
                                          >
                                            <TextHighlight
                                              text={nomeTela}
                                              termo={buscaDebounced}
                                            />
                                          </Typography>
                                        </Tooltip>
                                        {chavesCard.length > 0 && (
                                          <MarcadorTodos chaves={chavesCard} />
                                        )}
                                      </Box>

                                      <Divider className={classes.divisor} />

                                      {permsTelaSimples.length > 0 && (
                                        <Box className={classes.switchLinha}>
                                          {permsTelaSimples.map((permissao) => (
                                            <SwitchInput
                                              identificador={permissao.chave}
                                              key={permissao.chave}
                                              name={permissao.chave}
                                              label={
                                                <TextHighlight
                                                  text={permissao.acao}
                                                  termo={buscaDebounced}
                                                />
                                              }
                                            />
                                          ))}
                                        </Box>
                                      )}

                                      {gruposTree.map((grupo) => (
                                        <GrupoNode
                                          key={grupo.caminho}
                                          grupo={grupo}
                                          depth={0}
                                        />
                                      ))}
                                    </CardContent>
                                  </Card>
                                </Grid>
                              ),
                            )}
                          </Grid>
                        </ExpansionPanelDetails>
                      </ExpansionPanel>
                    ),
                  )}
                </Box>
              </ExpansionPanelDetails>
            </ExpansionPanel>
          </Box>
        ))}
      </TermoBuscaContext.Provider>
    </Box>
  );
}

const Dialogo = ({ onClose, open, id, isDialogoConfirmacaoActions }) => (
  <DialogoCreateEdit
    open={open}
    id={id}
    initialValues={initialValues}
    validators={validadores}
    resource="perfils"
    titleCreate="Perfil de Acesso"
    titleUpdate="Perfil de Acesso"
    nested={['permissoes']}
    onClose={onClose}
    transformGetDados={transformGetDados}
    transformSalvar={transformSalvar}
    isCloseTitle
    isDialogoConfirmacaoActions={isDialogoConfirmacaoActions}
    dialogProps={{
      fullScreen: true,
      /* fullWidth: true,
      maxWidth: 'lg', */
    }}
  >
    <Form />
  </DialogoCreateEdit>
);

export default Dialogo;
